/**
 * Gamification leaderboard page (tutors and establishments).
 */
import * as React from 'react';
import { Building2, Search, Users } from 'lucide-react';
import { ManagementPage } from '@/components/common/ManagementPage';
import { Spinner } from '@/components/ui/spinner';
import { DataTablePagination } from '@/components/common/data-table/DataTablePagination';
import { LeaderboardPodium } from '@/components/gamification/LeaderboardPodium';
import { TutorLeaderboardRow } from '@/components/gamification/TutorLeaderboardRow';
import { EstablishmentLeaderboardRow } from '@/components/gamification/EstablishmentLeaderboardRow';
import { LEADERBOARD_PAGE_SIZE } from '@/components/gamification/gamification-configs';
import {
  buildPodiumSlots,
  formatTutorCountLabel,
  leaderboardPageRange,
  shouldShowLeaderboardPodium,
  splitPodiumAndRest,
  useDebouncedValue,
} from '@/components/gamification/gamification-utils';
import { useQuery } from '@tanstack/react-query';
import { fetchEstablishmentLeaderboard, fetchLeaderboard } from '@/services/gamification.service';
import type { EstablishmentLeaderboardEntry, LeaderboardEntry } from '@/types/gamification.types';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import { getErrorMessage } from '@/utils/errors-utils';

/** Tutors vs establishments leaderboard tab. */
type LeaderboardTab = 'tutors' | 'establishments';

/** Podium card body for a tutor: display name + XP. */
function tutorPodiumSlotContent(entry: LeaderboardEntry): React.ReactNode {
  return (
    <div className="flex w-full flex-col items-center gap-1 text-center">
      <p className="w-full truncate text-sm font-semibold text-foreground">{entry.displayName}</p>
      <p className="text-sm font-bold text-[var(--color-1-nuance-1)]">{entry.totalXp} XP</p>
    </div>
  );
}

/** Podium card body for an establishment: name, city, XP, tutor count. */
function establishmentPodiumSlotContent(entry: EstablishmentLeaderboardEntry): React.ReactNode {
  return (
    <div className="flex w-full flex-col items-center gap-1 text-center">
      <p className="w-full truncate text-sm font-semibold text-foreground">{entry.displayName}</p>
      {entry.city ? (
        <p className="w-full truncate text-xs text-muted-foreground">{entry.city}</p>
      ) : null}
      <p className="text-sm font-bold text-[var(--color-1-nuance-1)]">{entry.totalXp} XP</p>
      {entry.tutorCount > 0 ? (
        <p className="text-xs text-muted-foreground">{formatTutorCountLabel(entry.tutorCount)}</p>
      ) : null}
    </div>
  );
}

/** Centered spinner used while a leaderboard query is in flight. */
function LeaderboardFetchingSpinner() {
  return (
    <div className="table-container">
      <div className="table-empty py-12">
        <Spinner size="lg" label="Chargement..." />
      </div>
    </div>
  );
}

/** Props for {@link LeaderboardSearchInput}. */
type LeaderboardSearchInputProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
}>;

/** Debounced-parent search field with a leading magnifier icon. */
function LeaderboardSearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: LeaderboardSearchInputProps) {
  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="h-10 w-full rounded-xl border border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] bg-[rgb(var(--card-rgb)/0.3)] pl-10 pr-4 text-sm focus:border-[var(--color-2-nuance-1)] focus:outline-none"
      />
    </div>
  );
}

/** Props for {@link LeaderboardErrorState}. */
type LeaderboardErrorStateProps = Readonly<{
  message: string;
  onRetry: () => void;
}>;

/** Query-error banner with a retry action. */
function LeaderboardErrorState({ message, onRetry }: LeaderboardErrorStateProps) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
      <p className="text-destructive-foreground">{message}</p>
      <button
        type="button"
        className="mt-2 text-sm font-medium text-[var(--color-1-nuance-1)] hover:underline"
        onClick={onRetry}
      >
        Réessayer
      </button>
    </div>
  );
}

/** Props for {@link LeaderboardPagination}. */
type LeaderboardPaginationProps = Readonly<{
  page: number;
  pageSize: number;
  total: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
}>;

/** Hide pagination when the result set is empty. */
function LeaderboardPagination({
  page,
  pageSize,
  total,
  itemLabel,
  onPageChange,
}: LeaderboardPaginationProps) {
  const range = leaderboardPageRange(page, pageSize, total);
  if (!range) return null;

  return (
    <DataTablePagination
      currentPage={page}
      totalPages={range.totalPages}
      totalItems={total}
      startIndex={range.startIndex}
      endIndex={range.endIndex}
      itemLabel={itemLabel}
      onPageChange={onPageChange}
    />
  );
}

/** Props for {@link TutorsLeaderboardPanel}. */
type TutorsLeaderboardPanelProps = Readonly<{
  page: number;
  search: string;
  onPageChange: (page: number) => void;
}>;

/** Tutors tab: podium on page 1 (no search), list, and pinned “my rank” when off-page. */
function TutorsLeaderboardPanel({ page, search, onPageChange }: TutorsLeaderboardPanelProps) {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['gamification', 'leaderboard', 'tutors', page, LEADERBOARD_PAGE_SIZE, search.trim()],
    queryFn: () =>
      fetchLeaderboard({ page, pageSize: LEADERBOARD_PAGE_SIZE, search: search.trim() }),
    placeholderData: (prev) => prev,
  });

  const entries = data?.entries ?? [];
  const isSearchActive = search.length > 0;
  const showPodium = shouldShowLeaderboardPodium(page, search);

  const { podium, rest } = React.useMemo(
    () => (showPodium ? splitPodiumAndRest(entries) : { podium: [], rest: entries }),
    [entries, showPodium],
  );

  /** Pin the viewer’s row below the list when it is not on the current page. */
  const showMyPositionOutsidePage = React.useMemo(() => {
    if (!data?.myEntry || data.myRank <= 0 || isSearchActive) return false;
    return !entries.some((e) =>
      data.myEntry?.userId ? e.userId === data.myEntry.userId : e.rank === data.myRank,
    );
  }, [data, entries, isSearchActive]);

  if (isLoading || isFetching) {
    return <LeaderboardFetchingSpinner />;
  }

  if (isError) {
    return (
      <LeaderboardErrorState
        message={getErrorMessage(error ?? 'loadLeaderboardFailed')}
        onRetry={() => void refetch()}
      />
    );
  }

  if (!data || data.total === 0) {
    return (
      <p className="text-muted-foreground">
        {isSearchActive
          ? 'Aucun tuteur trouvé pour cette recherche.'
          : 'Aucun tuteur dans le classement.'}
      </p>
    );
  }

  const podiumSlots = buildPodiumSlots(podium, tutorPodiumSlotContent);
  const myUserId = data.myEntry?.userId;

  return (
    <div className="space-y-4">
      {showPodium && Object.keys(podiumSlots).length > 0 ? (
        <LeaderboardPodium slots={podiumSlots} />
      ) : null}
      {rest.length > 0 ? (
        <ul className="m-0 list-none space-y-2 p-0" aria-label="Classement tuteurs">
          {rest.map((entry) => (
            <li key={entry.userId ?? `${entry.rank}-${entry.displayName}`}>
              <TutorLeaderboardRow
                entry={entry}
                isHighlighted={Boolean(myUserId && entry.userId === myUserId)}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {showMyPositionOutsidePage && data.myEntry ? (
        <div className="space-y-2 pt-4">
          <h3 className="text-sm font-semibold text-foreground">Votre position</h3>
          <TutorLeaderboardRow entry={data.myEntry} isHighlighted />
        </div>
      ) : null}
      <LeaderboardPagination
        page={data.page}
        pageSize={data.pageSize}
        total={data.total}
        itemLabel="tuteurs"
        onPageChange={onPageChange}
      />
    </div>
  );
}

/** Props for {@link EstablishmentsLeaderboardPanel}. */
type EstablishmentsLeaderboardPanelProps = Readonly<{
  page: number;
  search: string;
  onPageChange: (page: number) => void;
}>;

/** Establishments tab: podium on page 1 (no search), list, and pinned org row when off-page. */
function EstablishmentsLeaderboardPanel({
  page,
  search,
  onPageChange,
}: EstablishmentsLeaderboardPanelProps) {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      'gamification',
      'leaderboard',
      'establishments',
      page,
      LEADERBOARD_PAGE_SIZE,
      search.trim(),
    ],
    queryFn: () =>
      fetchEstablishmentLeaderboard({
        page,
        pageSize: LEADERBOARD_PAGE_SIZE,
        search: search.trim(),
      }),
    placeholderData: (prev) => prev,
  });

  const entries = data?.entries ?? [];
  const isSearchActive = search.length > 0;
  const showPodium = shouldShowLeaderboardPodium(page, search);

  const { podium, rest } = React.useMemo(
    () => (showPodium ? splitPodiumAndRest(entries) : { podium: [], rest: entries }),
    [entries, showPodium],
  );

  /** Pin the viewer’s establishment below the list when it is not on the current page. */
  const showMyPositionOutsidePage = React.useMemo(() => {
    if (!data?.myEntry || data.myRank <= 0 || isSearchActive) return false;
    return !entries.some((e) => e.establishmentId === data.myEntry?.establishmentId);
  }, [data, entries, isSearchActive]);

  if (isLoading || isFetching) {
    return <LeaderboardFetchingSpinner />;
  }

  if (isError) {
    return (
      <LeaderboardErrorState
        message={getErrorMessage(error ?? 'loadLeaderboardFailed')}
        onRetry={() => void refetch()}
      />
    );
  }

  if (!data || data.total === 0) {
    return (
      <p className="text-muted-foreground">
        {isSearchActive
          ? 'Aucun établissement trouvé pour cette recherche.'
          : 'Aucun établissement dans le classement.'}
      </p>
    );
  }

  const podiumSlots = buildPodiumSlots(podium, establishmentPodiumSlotContent);
  const myEstablishmentId = data.myEntry?.establishmentId;

  return (
    <div className="space-y-4">
      {showPodium && Object.keys(podiumSlots).length > 0 ? (
        <LeaderboardPodium slots={podiumSlots} />
      ) : null}
      {rest.length > 0 ? (
        <ul className="m-0 list-none space-y-2 p-0" aria-label="Classement établissements">
          {rest.map((entry) => (
            <li key={entry.establishmentId}>
              <EstablishmentLeaderboardRow
                entry={entry}
                isHighlighted={entry.establishmentId === myEstablishmentId}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {showMyPositionOutsidePage && data.myEntry ? (
        <div className="space-y-2 pt-4">
          <h3 className="text-sm font-semibold text-foreground">Votre établissement</h3>
          <EstablishmentLeaderboardRow entry={data.myEntry} isHighlighted />
        </div>
      ) : null}
      <LeaderboardPagination
        page={data.page}
        pageSize={data.pageSize}
        total={data.total}
        itemLabel="établissements"
        onPageChange={onPageChange}
      />
    </div>
  );
}

/** XP leaderboard page: tutors / establishments tabs, search, podium, and pagination. */
export const GamificationLeaderboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<LeaderboardTab>('tutors');
  const [searchInput, setSearchInput] = React.useState('');
  const [page, setPage] = React.useState(1);
  const debouncedSearch = useDebouncedValue(searchInput);

  /** Switching tabs clears search and returns to page 1. */
  React.useEffect(() => {
    setSearchInput('');
    setPage(1);
  }, [activeTab]);

  /** A new debounced query always restarts at page 1. */
  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const tabItems = [
    {
      id: 'tutors' as const,
      label: getRoleLabelPlural('tutor'),
      icon: <Users className="h-4 w-4" aria-hidden />,
    },
    {
      id: 'establishments' as const,
      label: 'Établissements',
      icon: <Building2 className="h-4 w-4" aria-hidden />,
    },
  ];

  const searchPlaceholder =
    activeTab === 'tutors'
      ? 'Rechercher un tuteur par nom ou email…'
      : 'Rechercher un établissement par nom ou ville…';

  const searchAriaLabel =
    activeTab === 'tutors'
      ? 'Rechercher un tuteur par nom ou email'
      : 'Rechercher un établissement par nom ou ville';

  return (
    <ManagementPage
      title="Classement (XP)"
      subtitle="Classement par points d'expérience des tuteurs et établissements de votre organisation"
      tabs={{
        items: tabItems,
        activeTab,
        onTabChange: (tabId) => setActiveTab(tabId as LeaderboardTab),
        ariaLabel: 'Type de classement',
        size: 'md',
        variant: 'default',
      }}
    >
      <div className="flex w-full flex-col gap-4">
        <LeaderboardSearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder={searchPlaceholder}
          ariaLabel={searchAriaLabel}
        />
        {activeTab === 'tutors' ? (
          <TutorsLeaderboardPanel page={page} search={debouncedSearch} onPageChange={setPage} />
        ) : (
          <EstablishmentsLeaderboardPanel
            page={page}
            search={debouncedSearch}
            onPageChange={setPage}
          />
        )}
      </div>
    </ManagementPage>
  );
};
